import { useState } from "react";
import { C } from "../constants/colors";
import { formatDate } from "../lib/utils";

const ARTIST_PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$0",
    period: "/ month",
    description: "Browse and apply to open calls across Australia. Always free.",
    features: [
      "Browse all opportunities",
      "Apply to unlimited calls",
      "Basic profile and portfolio (up to 6 images)",
      "Application tracker",
    ],
  },
  {
    id: "practice",
    name: "Practice",
    price: "$19",
    period: "/ month",
    description: "Tools to present yourself professionally and stay on top of opportunities.",
    features: [
      "Everything in Starter",
      "Unlimited portfolio images",
      "CV builder with PDF export",
      "Deadline reminders",
      "Saved opportunities",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: "$39",
    period: "/ month",
    description: "AI-powered tools to write stronger applications and win more opportunities.",
    features: [
      "Everything in Practice",
      "AI grant writing assistant",
      "AI artist statement generator",
      "Application success insights",
      "Priority profile visibility to galleries",
    ],
  },
];

const GALLERY_PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "/ month",
    description: "Get started with Open Call. Post up to 3 active calls at any time and see who's applying.",
    features: [
      "Up to 3 active listings",
      "Basic applicant list (names + emails)",
      "Application count per listing",
      "Standard listing placement",
    ],
  },
  {
    id: "emerging",
    name: "Emerging",
    price: "$79",
    period: "/ month",
    description: "For independent galleries and artist-run spaces ready to manage calls professionally.",
    features: [
      "Unlimited active listings",
      "Full application dashboard",
      "Artist profile + portfolio review",
      "In-platform artist messaging",
      "Shortlisting and status management",
      "Consignment agreement handling",
      "Basic analytics (views, application count per listing)",
      "14-day free trial",
    ],
  },
  {
    id: "established",
    name: "Established",
    price: "$149",
    period: "/ month",
    description: "For institutions and larger galleries that need audience intelligence, not just admin tools.",
    features: [
      "Everything in Emerging",
      "Advanced analytics — applicant demographics, state breakdowns, medium trends",
      "Featured placement in listings (pinned + badged)",
      "Priority in Open Call newsletter and social promotion",
      "Proactive artist directory browsing",
      "Quarterly report card with anonymised platform benchmarking",
      "14-day free trial",
    ],
  },
];

export function PlanManagement({ profile, userType, onUpgrade }) {
  const plans = userType === "gallery" ? GALLERY_PLANS : ARTIST_PLANS;

  const isOnPaidPlan =
    ["active", "trialing"].includes(profile?.subscription_status) &&
    ["emerging", "established"].includes(profile?.plan);

  const currentPlan = userType === "gallery"
    ? (isOnPaidPlan ? profile.plan : "free")
    : (profile?.plan || "starter");

  const [upgradingPlan, setUpgradingPlan] = useState(null);

  const handleUpgrade = async (planId) => {
    if (!onUpgrade) return;
    setUpgradingPlan(planId);
    try {
      await onUpgrade(planId);
    } finally {
      setUpgradingPlan(null);
    }
  };

  const now = new Date();
  const trialEnd = profile?.trial_ends_at ? new Date(profile.trial_ends_at) : null;
  const trialActive = trialEnd && trialEnd > now;
  const trialExpired = trialEnd && trialEnd <= now;
  const onFreePlan = currentPlan === "starter" || currentPlan === "free";

  const lbl = {
    fontSize: ".65rem",
    fontFamily: "'DM Mono',monospace",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: C.muted,
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.7rem", fontWeight: 400, color: C.bark, marginBottom: 6 }}>
        Your Plan
      </h1>
      <p style={{ fontSize: ".84rem", color: C.muted, marginBottom: 24, lineHeight: 1.65 }}>
        {userType === "gallery"
          ? "Manage your gallery subscription and billing."
          : "Manage your artist subscription and access to tools."}
      </p>

      {trialActive && (
        <div style={{ background: C.amberL, border: `1px solid ${C.amber}`, borderRadius: 8, padding: "12px 16px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: "1rem" }}>⏳</span>
          <div>
            <div style={{ fontSize: ".82rem", color: C.bark, fontWeight: 500 }}>
              Your free trial ends {formatDate(trialEnd.toISOString().slice(0,10))}
            </div>
            <div style={{ fontSize: ".75rem", color: C.muted, marginTop: 2 }}>
              Upgrade before your trial ends to keep access to all features.
            </div>
          </div>
        </div>
      )}

      {trialExpired && onFreePlan && (
        <div style={{ background: C.amberL, border: `1px solid ${C.amber}`, borderRadius: 8, padding: "12px 16px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: "1rem" }}>⚠</span>
          <div>
            <div style={{ fontSize: ".82rem", color: C.bark, fontWeight: 500 }}>Your trial has ended</div>
            <div style={{ fontSize: ".75rem", color: C.muted, marginTop: 2 }}>
              Upgrade to regain access to premium features.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${plans.length}, 1fr)`, gap: 16 }}>
        {plans.map(plan => {
          const isCurrent = plan.id === currentPlan;
          return (
            <div
              key={plan.id}
              style={{
                background: C.cream,
                border: `${isCurrent ? 2 : 1}px solid ${isCurrent ? C.clay : C.sand}`,
                borderRadius: 10,
                padding: "22px 20px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isCurrent && (
                <div style={{
                  position: "absolute",
                  top: -11,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: C.clay,
                  color: "#fff",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: ".6rem",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  padding: "3px 10px",
                  borderRadius: 20,
                  whiteSpace: "nowrap",
                }}>
                  Current plan
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <div style={{ ...lbl, marginBottom: 6 }}>{plan.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span style={{ fontFamily: "'Fraunces',serif", fontSize: "1.8rem", fontWeight: 400, color: C.bark, lineHeight: 1 }}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span style={{ fontSize: ".72rem", color: C.muted, fontFamily: "'DM Mono',monospace" }}>{plan.period}</span>
                  )}
                </div>
                {plan.description && (
                  <p style={{ fontSize: ".78rem", color: C.muted, lineHeight: 1.55, margin: "10px 0 0" }}>
                    {plan.description}
                  </p>
                )}
              </div>

              <ul style={{ listStyle: "none", margin: 0, padding: 0, flex: 1, marginBottom: 20 }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{ fontSize: ".8rem", color: C.bark, lineHeight: 1.6, paddingLeft: 16, position: "relative", marginBottom: 4 }}>
                    <span style={{ position: "absolute", left: 0, color: C.sage }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div style={{ textAlign: "center", fontSize: ".72rem", fontFamily: "'DM Mono',monospace", color: C.muted, padding: "9px 0", borderTop: `1px solid ${C.sand}` }}>
                  Active
                </div>
              ) : plan.id === "free" || plan.id === "starter" ? (
                <div style={{ textAlign: "center", fontSize: ".72rem", fontFamily: "'DM Mono',monospace", color: C.mutedL, padding: "9px 0", borderTop: `1px solid ${C.sand}` }}>
                  Always available
                </div>
              ) : (
                <button
                  className="btn-p"
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={!!upgradingPlan}
                  style={{ width: "100%", fontSize: ".72rem", padding: "9px 12px" }}
                >
                  {upgradingPlan === plan.id ? "Redirecting…" : "Upgrade →"}
                </button>
              )}
            </div>
          );
        })}
      </div>


    </div>
  );
}
